import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const HERO_IMG = "https://images.unsplash.com/photo-1764009184594-327e5f5fd788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxhbHVtaW5pdW0lMjB3aW5kb3clMjBmcmFtZSUyMG1vZGVybiUyMGJ1aWxkaW5nJTIwZmFjYWRlJTIwZGF5dGltZXxlbnwwfHx8fDE3Nzc5MjQ4NTB8MA&ixlib=rb-4.1.0&q=85";
const WHATSAPP_URL = "https://wa.me/919827333552?text=" + encodeURIComponent("Hi, I'm interested in your aluminium solutions. Can you share more details?");

export default function HeroSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.5], [0, -50]);

  return (
    <section
      id="hero"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden"
    >
      {/* Fullscreen parallax background */}
      <motion.div className="absolute inset-0" style={{ scale: imgScale }}>
        <img src={HERO_IMG} alt="Modern aluminium facade" className="w-full h-full object-cover" loading="eager" />
      </motion.div>
      {/* Dark cinematic gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

      {/* Content */}
      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 h-full flex items-center"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            {/* Overline */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex items-center gap-4 mb-8"
            >
              <div className="w-12 h-px bg-[#D4AF37]" />
              <span className="text-xs tracking-[0.4em] uppercase text-[#D4AF37] font-medium">
                Aluminizing Trust Since 1998
              </span>
            </motion.div>

            {/* Main headline — BIG */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.9] text-white font-['Oswald']"
            >
              High Square
              <br />
              <span className="gold-text">Aluminium</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="text-xs tracking-[0.15em] uppercase text-white/60 mt-4 mb-8"
            >
              A Venture by Monalisa Aluminium
            </motion.p>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              className="text-lg md:text-xl text-white/80 leading-relaxed max-w-lg mb-3"
            >
              Precision Aluminium Solutions for Modern Spaces
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2 }}
              className="text-base text-white/50 leading-relaxed max-w-lg mb-8"
            >
              Premium Windows, Doors & Custom Fabrication for Modern Homes and Commercial Projects
            </motion.p>

            {/* Trust line */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.4 }}
              className="flex items-center gap-6 mb-10"
            >
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold font-['Oswald'] text-[#D4AF37]">25+</span>
                <span className="text-xs text-white/60 uppercase tracking-wider">Years</span>
              </div>
              <div className="w-px h-6 bg-white/20" />
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold font-['Oswald'] text-[#D4AF37]">1000+</span>
                <span className="text-xs text-white/60 uppercase tracking-wider">Projects</span>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.6 }}
              className="flex flex-wrap gap-4"
            >
              <a
                href="#contact"
                data-testid="hero-explore-btn"
                className="btn-premium bg-[#D4AF37] text-[#0A0A0A] px-10 py-4 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#E8C94B]"
              >
                Get Free Quote
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-quote-btn"
                className="btn-premium border border-white/30 text-white hover:bg-white/10 px-10 py-4 text-xs tracking-[0.2em] uppercase font-semibold flex items-center gap-2"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp Now
              </a>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10" />
    </section>
  );
}
